import { useState } from "react";
import { tokens } from "@core/theme/tokens";
import {
  DialogHeader,
  LanguageSwitch,
  Modal,
  ModalCloseFooter,
  type ContentLanguage,
} from "@portal/common";
import type { TemplatePreview as TemplatePreviewResult } from "./api";

// SPEC-218 §7, criterio 8, resolución humana PA-036-C: el HTML que llega en
// `preview.es`/`preview.en` es el documento COMPLETO dentro del marco de
// marca real, compuesto por el backend
// (`api/src/infrastructure/notifications/message-template-brand-frame.ts`).
// El portal nunca lo inyecta en su propio DOM ni lo replica: si lo hiciera,
// lo que se ve dejaría de ser el correo que efectivamente sale. Se renderiza
// aislado en un `iframe` con `srcDoc` y `sandbox` (sin scripts).
export function TemplatePreviewDialog({
  open,
  preview,
  bilingual,
  onClose,
}: {
  open: boolean;
  preview: TemplatePreviewResult | undefined;
  // PA-036-D: las tres plantillas solo en español no ofrecen alternar
  // idioma en la previsualización (`preview-language-toggle` oculto).
  bilingual: boolean;
  onClose: () => void;
}) {
  const [language, setLanguage] = useState<ContentLanguage>("es");
  if (!open) return null;

  const html = preview ? preview[language] : undefined;

  return (
    // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: las tres regiones del
    // `Modal`. El conmutador ES/EN sube al encabezado FIJO junto al título
    // —es lo que más se gana acá: el marco de marca mide 480px de alto y, con
    // el panel entero scrolleando, alternar idioma exigía volver arriba—.
    <Modal
      label="Previsualización"
      width={720}
      onClose={onClose}
      // Punto #21 (revisión en vivo 2026-09-22): encabezado común de
      // diálogos; el conmutador de idioma sigue a la derecha.
      header={
        <DialogHeader
          eyebrow="Plantillas de mensaje"
          title="Previsualización"
          aside={
            bilingual ? (
              <LanguageSwitch value={language} onChange={setLanguage} />
            ) : null
          }
        />
      }
      footer={<ModalCloseFooter onClose={onClose} />}
    >
      <div
        style={{
          border: `1px solid ${tokens.surface.line2}`,
          borderRadius: 14,
          overflow: "hidden",
          background: "#fff",
          height: 480,
        }}
      >
        {html === undefined ? (
          <div style={{ padding: 24, color: tokens.text.tertiary }}>
            Generando previsualización…
          </div>
        ) : (
          <iframe
            title="Vista previa · marco de marca"
            srcDoc={html}
            sandbox=""
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        )}
      </div>
    </Modal>
  );
}
